/* eslint-disable react/no-array-index-key */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { isEmpty } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { FolderOffOutlined, Settings } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { DateRangePicker, SingleInputDateRangeField } from '@mui/x-date-pickers-pro'

import { titleize, underscore } from 'inflection'
import { DateTime } from 'luxon'

import { Breadcrumbs, Loading } from '@common/components'
import {
  formatCurrency,
  generateColors,
  isAbortError,
  parseApiErrors,
  useSmallScreen,
} from '@common/utils'
import EntitySelector from '@portal/pages/Dashboards/SmokeMetrics/components/EntitySelector'
import homeUrls from '@portal/pages/Home/urls'
import propertyUrls from '@portal/pages/Properties/urls'
import ListPageTitle from '@portal/UI/components/ListPageTitle'

import NegativeReviewsChart from './NegativeReviewsChart'

const stickyStyle = {
  position: 'sticky',
  left: 0,
  zIndex: 800,
  background: 'white',
  maxWidth: 340,
  minWidth: 240,
}

const headersMap = [
  { data: 'eventsCharged', total: 'totalEventsCharged', symbol: '' },
  { data: 'netChargeRate', total: 'totalNetChargeRate', symbol: '%' },
  { data: 'netCharges', total: 'totalNetCharges', symbol: '$' },
  { data: 'reviewCount', total: 'totalReviews', symbol: '' },
  { data: 'negativeReviewCount', total: 'totalNegativeReviews', symbol: '' },
  { data: 'negativeReviewRate', total: 'totalNegativeReviewRate', symbol: '%' },
  {
    data: 'negativeSmokingFeeReviewCount',
    total: 'totalNegativeSmokingFeeReviews',
    symbol: '',
  },
  {
    data: 'negativeSmokingFeeReviewRate',
    total: 'totalNegativeSmokingFeeReviewRate',
    symbol: '%',
  },
  {
    data: 'negativeSmokingFeeNegativeReviewRate',
    total: 'totalNegativeSmokingFeeNegativeReviewRate',
    symbol: '%',
  },
  {
    data: 'negativeSmokingSmellReviewCount',
    total: 'totalNegativeSmokingSmellReviews',
    symbol: '',
  },
  {
    data: 'negativeSmokingSmellReviewRate',
    total: 'totalNegativeSmokingSmellReviewRate',
    symbol: '%',
  },
  {
    data: 'negativeSmokingSmellNegativeReviewRate',
    total: 'totalNegativeSmokingSmellNegativeReviewRate',
    symbol: '%',
  },
  {
    data: 'profitPerNegativeSmokingFeeReview',
    total: 'totalProfitPerNegativeSmokingFeeReview',
    symbol: '$',
  },
  {
    data: 'negativeSmokingFeeChargedEventRate',
    total: 'totalNegativeSmokingFeeChargedEventRate',
    symbol: '%',
  },
]

const hasValue = (value) => value !== null && value !== undefined

const RANGE_PICKER_WIDTH = 275

export default function ReviewAnalysisDashboard() {
  const defaultEnd = DateTime.now()
  const defaultStart = DateTime.now().minus({ days: 30 })

  const [eventRangeValue, setEventRangeValue] = useState([defaultStart, defaultEnd])
  const [reviewRangeValue, setReviewRangeValue] = useState([defaultStart, defaultEnd])

  const [currentEntity, setCurrentEntity] = useState('organization')
  const [settingsAnchor, setSettingsAnchor] = useState(null)
  const [filterUnreviewed, setFilterUnreviewed] = useState(true)

  const [selectedOrganizations, setSelectedOrganizations] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [selectedProperties, setSelectedProperties] = useState([])
  const [selectedPropertyGroups, setSelectedPropertyGroups] = useState([])

  const {
    reviewAnalysis,
    reviewAnalysisIsLoading,
    allOrganizations,
    allAccounts,
    allPropertyGroups,
    allProperties,
    doFetchReviewAnalysis,
    doResetReviewAnalysisState,
    doOrganizationListFetchAll,
    doAccountListFetchAll,
    doPropertyGroupsListFetchAll,
    doPropertyListFetchAll,
    doShowSnackbar,
  } = useConnect(
    'selectReviewAnalysis',
    'selectReviewAnalysisIsLoading',
    'selectAllOrganizations',
    'selectAllAccounts',
    'selectAllPropertyGroups',
    'selectAllProperties',
    'doFetchReviewAnalysis',
    'doResetReviewAnalysisState',
    'doOrganizationListFetchAll',
    'doAccountListFetchAll',
    'doPropertyGroupsListFetchAll',
    'doPropertyListFetchAll',
    'doShowSnackbar',
  )

  const isSmallScreen = useSmallScreen()

  const filteredReviewResults = useMemo(() => {
    if (!filterUnreviewed) return reviewAnalysis?.results ?? []

    if (reviewAnalysis?.results?.length) {
      return reviewAnalysis.results.filter(
        (item) =>
          item.negativeReviewCount ||
          item.negativeSmokingFeeReviewCount ||
          item.negativeSmokingSmellReviewCount ||
          item.reviewCount,
      )
    }
    return []
  }, [reviewAnalysis, filterUnreviewed])

  const filteredTotalEvents = useMemo(
    () =>
      filterUnreviewed
        ? filteredReviewResults.reduce((acc, item) => acc + item.eventCount, 0)
        : reviewAnalysis?.totalEvents,
    [filterUnreviewed, filteredReviewResults, reviewAnalysis],
  )

  const filteredNegativeSmokeReviewOfTotalCharged = useMemo(() => {
    let totalEvents = null
    let totalNegativeReviews = null

    if (filterUnreviewed) {
      totalEvents = filteredReviewResults.reduce(
        (acc, item) => acc + item.eventCount,
        0,
      )
      totalNegativeReviews = filteredReviewResults.reduce(
        (acc, item) => acc + item.negativeSmokingFeeReviewCount,
        0,
      )
    } else {
      totalEvents = reviewAnalysis?.totalEvents
      totalNegativeReviews = reviewAnalysis?.totalNegativeSmokingFeeReviews
    }

    if (hasValue(totalEvents) && hasValue(totalNegativeReviews)) {
      const value = totalNegativeReviews / totalEvents

      const roundUp = (num, precision) => {
        const formattedPrecision = 10 ** precision
        return Math.ceil(num * formattedPrecision) / formattedPrecision
      }
      const result = isNaN(value) ? 0 : roundUp(value, 1)
      return `${result}%`
    }
    return null
  }, [reviewAnalysis, filteredReviewResults, filterUnreviewed])

  const hasSelectedEntities = () =>
    !isEmpty(selectedOrganizations) ||
    !isEmpty(selectedAccounts) ||
    !isEmpty(selectedProperties) ||
    !isEmpty(selectedPropertyGroups)

  const entityMap = useMemo(
    () => ({
      organization: {
        fetch: doOrganizationListFetchAll,
        current: allOrganizations,
        selected: selectedOrganizations,
        setSelected: setSelectedOrganizations,
      },
      account: {
        fetch: doAccountListFetchAll,
        current: allAccounts,
        selected: selectedAccounts,
        setSelected: setSelectedAccounts,
        groupBy: (opt) => opt.organizationName,
        sortByFields: ['organizationName', 'name'],
      },
      propertyGroup: {
        fetch: doPropertyGroupsListFetchAll,
        current: allPropertyGroups,
        selected: selectedPropertyGroups,
        setSelected: setSelectedPropertyGroups,
      },
      property: {
        fetch: doPropertyListFetchAll,
        current: allProperties,
        selected: selectedProperties,
        setSelected: setSelectedProperties,
        groupBy: (opt) => opt.accountName,
        sortByFields: ['accountName', 'name'],
      },
    }),
    [
      doOrganizationListFetchAll,
      doAccountListFetchAll,
      doPropertyGroupsListFetchAll,
      doPropertyListFetchAll,
      allOrganizations,
      allAccounts,
      allPropertyGroups,
      allProperties,
      selectedOrganizations,
      selectedAccounts,
      selectedPropertyGroups,
      selectedProperties,
      setSelectedOrganizations,
      setSelectedAccounts,
      setSelectedPropertyGroups,
      setSelectedProperties,
    ],
  )

  const fetchEntities = async (entity) => {
    try {
      entityMap[entity].fetch({ active: true })
    } catch (err) {
      if (!isAbortError(err)) {
        const parsedError = parseApiErrors(err?.response)
        doShowSnackbar(parsedError, 'error')
      }
    }
  }

  const resetAdjacentEntities = (entity) =>
    Object.keys(entityMap)
      .filter((e) => e !== entity)
      .forEach((ent) => entityMap[ent].setSelected([]))

  const renderValue = ({ value, symbol }) => {
    if (value === null || value === undefined) return '--'
    if (!symbol) return value
    switch (symbol) {
      case '$':
        return formatCurrency(value)
      case '%':
        return `${value}%`
      default:
        return value
    }
  }

  const fetchAnalysis = async ({ organizations } = {}) => {
    try {
      const canFetch =
        (organizations || hasSelectedEntities()) &&
        eventRangeValue.every((value) => value !== null && value !== undefined) &&
        reviewRangeValue.every((value) => value !== null && value !== undefined)

      if (canFetch) {
        await doFetchReviewAnalysis({
          eventStart: eventRangeValue[0],
          eventEnd: eventRangeValue[1],
          reviewPublishedStart: reviewRangeValue[0],
          reviewPublishedEnd: reviewRangeValue[1],
          organizations: organizations ?? selectedOrganizations,
          accounts: selectedAccounts,
          propertyGroups: selectedPropertyGroups,
          properties: selectedProperties,
        })
      }
    } catch (err) {
      if (!isAbortError(err)) {
        const parsedError = parseApiErrors(err?.response)
        doShowSnackbar(parsedError, 'error')
      }
    }
  }

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const orgs = await doOrganizationListFetchAll({ active: true })
        if (!orgs) return

        if (orgs.payload.length > 0) {
          setSelectedOrganizations(orgs.payload)
          fetchAnalysis({ organizations: orgs.payload })
        }
      } catch (err) {
        const parsedError = parseApiErrors(err)
        doShowSnackbar(parsedError, 'error')
      }
    }
    fetchOrgs()
    return () => doResetReviewAnalysisState()
  }, [])

  useEffect(() => {
    fetchAnalysis()
  }, [eventRangeValue, reviewRangeValue])

  const DetailItem = useCallback(
    ({ label, value }) => (
      <Box display="flex" gap={0.5}>
        <Typography fontWeight="bold" fontSize={isSmallScreen.mobile ? 12 : 14}>
          {label}:
        </Typography>
        <Typography fontSize={isSmallScreen.mobile ? 12 : 14}>
          {value ?? '--'}
        </Typography>
      </Box>
    ),
    [],
  )

  const chartData = useMemo(() => {
    if (filteredReviewResults.length) {
      const colors = generateColors(filteredReviewResults.length)
      return filteredReviewResults.map((item, i) => ({
        name: item.propertyName,
        color: colors[i],
        x: item.negativeReviewRate,
        y: item.negativeSmokingFeeNegativeReviewRate,
      }))
    }
    return []
  }, [filteredReviewResults])

  return (
    <>
      <Popover
        open={!!settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorEl={settingsAnchor}
        anchorOrigin={{ vertical: 44, horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack direction="column" px={2} py={0.5}>
          <FormControlLabel
            disabled={reviewAnalysisIsLoading}
            slotProps={{ typography: { fontSize: 14 } }}
            control={
              <Checkbox
                checked={filterUnreviewed}
                size="small"
                onChange={() => setFilterUnreviewed((v) => !v)}
              />
            }
            label="Hide properties with no reviews during period"
          />
        </Stack>
      </Popover>

      <Box m={3} display="flex" flexDirection="column">
        <Breadcrumbs
          links={[
            { label: 'Home', href: homeUrls.home },
            { label: 'Review Analysis Dashboard' },
          ]}
        />
        <ListPageTitle title="Review Analysis Dashboard" mb={2} />
        <Box display="flex">
          <Box ml={isSmallScreen.mobile ? 0 : 2} flex={1} overflow="hidden">
            <Box
              display="flex"
              flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
              my={1}
              gap={isSmallScreen.mobile ? 4 : 0}
              justifyContent="space-between"
            >
              <Box
                display="flex"
                flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
                alignItems={isSmallScreen.mobile ? 'center' : 'end'}
                gap={2}
              >
                <EntitySelector
                  entityMap={entityMap}
                  currentEntity={currentEntity}
                  onSave={fetchAnalysis}
                  onOpen={() => fetchEntities(currentEntity)}
                  onEntityTypeChange={(type) => {
                    resetAdjacentEntities(type)
                    setCurrentEntity(type)
                  }}
                  onEntitiesChange={(entity) => {
                    if (isEmpty(entity)) {
                      doResetReviewAnalysisState()
                    }
                    entityMap[currentEntity].setSelected(entity)
                  }}
                  disabled={reviewAnalysisIsLoading}
                />
                <Button
                  fullWidth={isSmallScreen.mobile}
                  variant="outlined"
                  disabled={reviewAnalysisIsLoading}
                  startIcon={<Settings />}
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                >
                  Settings
                </Button>
              </Box>

              <Box display="flex" flexDirection="column" gap={1.5}>
                <DateRangePicker
                  disableMaskedInput
                  value={eventRangeValue}
                  maxDate={defaultEnd}
                  onChange={setEventRangeValue}
                  disabled={reviewAnalysisIsLoading}
                  slots={{ field: SingleInputDateRangeField }}
                  slotProps={{
                    textField: {
                      label: 'Event range',
                      size: 'small',
                      style: {
                        width: isSmallScreen.mobile ? '100%' : RANGE_PICKER_WIDTH,
                      },
                      required: true,
                    },
                  }}
                />
                <DateRangePicker
                  disableMaskedInput
                  value={reviewRangeValue}
                  maxDate={defaultEnd}
                  onChange={setReviewRangeValue}
                  disabled={reviewAnalysisIsLoading}
                  slots={{ field: SingleInputDateRangeField }}
                  slotProps={{
                    textField: {
                      label: 'Review Published Range',
                      size: 'small',
                      style: {
                        width: isSmallScreen.mobile ? '100%' : RANGE_PICKER_WIDTH,
                      },
                      required: true,
                    },
                  }}
                />
              </Box>
            </Box>

            {reviewAnalysisIsLoading && <Loading />}
            {!hasSelectedEntities() && !reviewAnalysisIsLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="calc(100vh - 300px)"
              >
                <Typography variant="h4" color="grey">
                  No Selected Entities
                </Typography>
              </Box>
            )}
            {hasSelectedEntities() && !reviewAnalysisIsLoading && (
              <Box py={1}>
                {!!filteredReviewResults.length && (
                  <>
                    <DetailItem
                      label="Total charged events during period"
                      value={filteredTotalEvents}
                    />
                    <DetailItem
                      label="Negative smoke review as % of total charged events"
                      value={filteredNegativeSmokeReviewOfTotalCharged}
                    />
                  </>
                )}
                {filteredReviewResults.length ? (
                  <TableContainer component={Paper} sx={{ my: 2, mx: 0.1 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={stickyStyle}>Property</TableCell>
                          {headersMap.map(({ data }) => (
                            <TableCell
                              key={data}
                              style={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                            >
                              {titleize(underscore(data))}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredReviewResults?.map((data) => (
                          <TableRow key={data.propertyId}>
                            <TableCell style={stickyStyle}>
                              <Link
                                href={propertyUrls.entity.replace(
                                  ':id',
                                  data.propertyId,
                                )}
                              >
                                <Typography noWrap variant="body2">
                                  {data.propertyName}
                                </Typography>
                              </Link>
                            </TableCell>
                            {headersMap.map(({ data: header, symbol }, i) => (
                              <TableCell
                                key={`${header}_${i}`}
                                style={{ textAlign: 'right' }}
                              >
                                <Box display="flex" justifyContent="flex-end">
                                  {renderValue({ value: data[header], symbol })}
                                </Box>
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell style={stickyStyle}>Total</TableCell>
                          {headersMap.map(({ total: header, symbol }) => (
                            <TableCell key={header} style={{ textAlign: 'right' }}>
                              <Box
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                {renderValue({
                                  value: reviewAnalysis?.[header],
                                  symbol,
                                })}
                              </Box>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="calc(100vh - 300px)"
                  >
                    <FolderOffOutlined sx={{ color: 'grey', fontSize: 40 }} />
                    <Typography variant="h5" color="grey">
                      No Review Analysis Found
                    </Typography>
                  </Box>
                )}
                {!isEmpty(chartData) && (
                  <NegativeReviewsChart
                    data={chartData}
                    height={350}
                    xLabel="Negative Review Rate"
                    yLabel="Negative Smoking Fee Negative Review Rate"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}
